import React, { useState } from "react";
import { startCase } from "lodash";

import { makeStyles } from "@material-ui/core";
import pluralize from "pluralize";
import { Button } from "semantic-ui-react";

import { useBooleanState } from "hooks";
import { useFeatures } from "components/FeatureFlag";
import Table from "components/Table";

import { useSharedProcesses } from "./hooks";
import { ShareResourcesModal } from "../ShareResourcesModal";
import { ShareResourceHistoryModal } from "./components";

const useStyles = makeStyles({
  table: {
    border: "1px solid #DADADA",
    borderRadius: "4px",
  },
  headerCell: {
    backgroundColor: "#F9F9F9",
    fontSize: "14px",
    fontWeight: "bold",
    padding: "0.92857143em 0.78571429em",
  },
  bodyCell: {
    fontSize: "14px",
    padding: "0.78571429em 0.78571429em",
  },
});

function MySharedResources() {
  const classes = useStyles();
  const [selectedSharedResource, setSelectedSharedResource] = useState(null);
  const [
    isShareResourcesModalOpen,
    openShareResourcesModal,
    closeShareResourcesModal,
  ] = useBooleanState(false);

  const {
    sharedProcesses,
    initialLoading,
    isReloading,
    totalCount,
    pageControl,
    updatePageControl,
    reloadData,
  } = useSharedProcesses();

  const sharedResourcesFlag = useFeatures("shared-resources");

  const tableColumns = ["name", "timesShared"].map((id, index) => {
    return {
      id: id,
      label:
        sharedResourcesFlag?.settings?.myShareTab?.table?.columns[index] ||
        startCase(id),
    };
  });

  const handleTimesSharedClicked = (sharedProcess) => {
    if (sharedProcess.timesShared > 0) {
      setSelectedSharedResource(sharedProcess);
    }
  };

  return (
    <React.Fragment>
      {sharedResourcesFlag?.settings?.myShareTab?.description && (
        <div
          style={{ margin: "0 0 14px 0" }}
          data-test="my-shared-resources-description"
        >
          <span>{sharedResourcesFlag.settings.myShareTab.description}</span>
        </div>
      )}
      <Table className={classes.table} data-test="my-shared-resources-table">
        <Table.Content>
          <Table.Header
            columns={tableColumns}
            className={classes.headerCell}
            rowCount={sharedProcesses?.length}
          />
          <Table.Body
            isLoading={initialLoading}
            isReloading={isReloading}
            columns={tableColumns}
            data={sharedProcesses?.map((sharedProcess) => {
              return {
                ...sharedProcess,
                timesShared: (
                  <div
                    onClick={() => {
                      handleTimesSharedClicked(sharedProcess);
                    }}
                    data-test="shared-with"
                    style={{
                      fontSize: "12px",
                      textDecoration:
                        sharedProcess.timesShared > 0 ? "underline" : "",
                      cursor:
                        sharedProcess.timesShared > 0 ? "pointer" : "default",
                    }}
                  >
                    {`${sharedProcess.timesShared} ${pluralize(
                      "recipient",
                      sharedProcess.timesShared
                    )}`}
                  </div>
                ),
              };
            })}
            className={classes.bodyCell}
            emptyDataMessage={
              sharedResourcesFlag?.settings?.myShareTab?.table?.emptyMessage ||
              "You haven't shared any resources yet"
            }
          />
        </Table.Content>
      </Table>
      <Table.PaginationControl
        isLoading={initialLoading || isReloading}
        totalCount={totalCount}
        page={pageControl.page}
        pageSize={pageControl.pageSize}
        onPageChange={updatePageControl}
        onPageSizeChange={updatePageControl}
      />

      <Button primary onClick={openShareResourcesModal}>
        {sharedResourcesFlag?.settings?.myShareTab?.shareButton ||
          "Share Resources"}
      </Button>

      {isShareResourcesModalOpen && (
        <ShareResourcesModal
          onSubmit={() => {
            reloadData();
            closeShareResourcesModal();
          }}
          onClose={closeShareResourcesModal}
        />
      )}

      {selectedSharedResource && (
        <ShareResourceHistoryModal
          resource={selectedSharedResource}
          onClose={() => {
            return setSelectedSharedResource(null);
          }}
        />
      )}
    </React.Fragment>
  );
}

export default MySharedResources;
